import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Security, PrivacyTip, Cookie, ContactSupport, Settings } from '@mui/icons-material';
import privacyPolicyData from '../assets/privacyPolicy.json';

const PolicyPage = () => {
  const translations = useSelector((state) => state.language.translations);
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicyData = async () => {
      try {
        // In the future, replace this with actual API call
        // const response = await apiRequest({ url: PRIVACY_POLICY_ENDPOINT });
        // setPolicyData(response.data);
        
        // For now, using the JSON file
        setPolicyData(privacyPolicyData);
      } catch (error) {
        console.error('Error fetching privacy policy:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicyData();
  }, []);


  if (loading) {
    return (
      <div className="bg-[#FFECE5] min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-custom-orange"></div>
      </div>
    );
  }

  if (!policyData) {
    return (
      <div className="bg-[#FFECE5] min-h-screen flex items-center justify-center">
        <p className="text-custom-maroon">Failed to load privacy policy</p>
      </div>
    );
  }

  return (
    <div className="bg-[#FFECE5] min-h-screen py-16 px-4 sm:px-8">
      {/* Decorative Top Border */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-custom-orange via-custom-maroon to-custom-brown"></div>

      <div className="max-w-4xl mx-auto relative">
        {/* Header Section with Enhanced Design */}
        <div className="text-center mb-16 relative mt-8">
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-24 h-24 bg-custom-light-beige rounded-full opacity-50"></div>
          <h1 className="text-4xl md:text-5xl font-bold text-custom-maroon mb-4 relative z-10">
            Privacy Policy
          </h1>
          <div className="w-24 h-1 bg-custom-orange mx-auto mb-4"></div>
          {/* <p className="text-custom-brown text-lg">
            Last updated: {new Date().toLocaleDateString()}
          </p> */}
        </div>

        {/* Main Content with Enhanced Cards */}
        <div className="space-y-8">
          {/* Introduction Card */}
          <div className="bg-custom-beige rounded-2xl p-8 shadow-lg transform hover:scale-[1.01] transition-transform duration-300">
            <div className="flex items-center mb-6">
              <div className="bg-custom-light-beige p-3 rounded-full mr-4">
                <Security className="text-custom-maroon text-2xl" />
              </div>
              <div>
                <span className="text-custom-orange font-semibold text-lg mb-1 block">
                  {policyData.sections[0].number}
                </span>
                <h2 className="text-2xl font-semibold text-custom-maroon">{policyData.sections[0].title}</h2>
              </div>
            </div>
            <div className="space-y-4 text-custom-gray leading-relaxed">
              {policyData.sections[0].content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>

          {/* Information Collection Card */}
          <div className="bg-custom-beige rounded-2xl p-8 shadow-lg transform hover:scale-[1.01] transition-transform duration-300">
            <div className="flex items-center mb-6">
              <div className="bg-custom-light-beige p-3 rounded-full mr-4">
                <PrivacyTip className="text-custom-maroon text-2xl" />
              </div>
              <div>
                <span className="text-custom-orange font-semibold text-lg mb-1 block">
                  {policyData.sections[1].number}
                </span>
                <h2 className="text-2xl font-semibold text-custom-maroon">{policyData.sections[1].title}</h2>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {policyData.sections[1].subsections.map((subsection, index) => (
                <div key={index} className="bg-white/50 rounded-xl p-4">
                  <h3 className="font-semibold text-custom-maroon mb-2">{subsection.title}</h3>
                  <ul className="list-disc pl-6 space-y-2 text-custom-gray">
                    {subsection.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* How We Use Information Card */}
          <div className="bg-custom-beige rounded-2xl p-8 shadow-lg transform hover:scale-[1.01] transition-transform duration-300">
            <div className="flex items-center mb-6">
              <div className="bg-custom-light-beige p-3 rounded-full mr-4">
                <ContactSupport className="text-custom-maroon text-2xl" />
              </div>
              <div>
                <span className="text-custom-orange font-semibold text-lg mb-1 block">
                  {policyData.sections[2].number}
                </span>
                <h2 className="text-2xl font-semibold text-custom-maroon">{policyData.sections[2].title}</h2>
              </div>
            </div>
            <div className="space-y-4">
              {policyData.sections[2].items.map((item, index) => (
                <div key={index} className="bg-white/50 rounded-xl p-4 flex items-start">
                  <div className="w-2 h-2 bg-custom-orange rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-custom-gray leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cookies Section Card */}
          <div className="bg-custom-beige rounded-2xl p-8 shadow-lg transform hover:scale-[1.01] transition-transform duration-300">
            <div className="flex items-center mb-6">
              <div className="bg-custom-light-beige p-3 rounded-full mr-4">
                <Cookie className="text-custom-maroon text-2xl" />
              </div>
              <div>
                <span className="text-custom-orange font-semibold text-lg mb-1 block">
                  {policyData.sections[3].number}
                </span>
                <h2 className="text-2xl font-semibold text-custom-maroon">{policyData.sections[3].title}</h2>
              </div>
            </div>
            <div className="bg-white/50 rounded-xl p-6">
              {policyData.sections[3].content.map((paragraph, index) => (
                <p key={index} className="text-custom-gray leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* New Section: Controlling Personal Information */}
          <div className="bg-custom-beige rounded-2xl p-8 shadow-lg transform hover:scale-[1.01] transition-transform duration-300">
            <div className="flex items-center mb-6">
              <div className="bg-custom-light-beige p-3 rounded-full mr-4">
                <Settings className="text-custom-maroon text-2xl" />
              </div>
              <div>
                <span className="text-custom-orange font-semibold text-lg mb-1 block">
                  {policyData.sections[4].number}
                </span>
                <h2 className="text-2xl font-semibold text-custom-maroon">{policyData.sections[4].title}</h2>
              </div>
            </div>
            
            <div className="bg-white/50 rounded-xl p-6">
              <p className="text-custom-gray leading-relaxed mb-6">
                {policyData.sections[4].intro}
              </p>
              
              <div className="space-y-6">
                {policyData.sections[4].subsections.map((subsection, index) => (
                  <div key={index} className="bg-custom-light-beige/50 rounded-lg p-5">
                    <h3 className="font-semibold text-custom-maroon mb-3">{subsection.title}</h3>
                    <p className="text-custom-gray leading-relaxed">{subsection.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-custom-light-beige rounded-2xl p-8 shadow-lg transform hover:scale-[1.01] transition-transform duration-300">
            <div className="flex items-center mb-6">
              <div className="bg-white/50 p-3 rounded-full mr-4">
                <ContactSupport className="text-custom-maroon text-2xl" />
              </div>
              <div>
                <span className="text-custom-orange font-semibold text-lg mb-1 block">
                  {policyData.sections[5].number}
                </span>
                <h2 className="text-2xl font-semibold text-custom-maroon">{policyData.sections[5].title}</h2>
              </div>
            </div>
            <div className="bg-white/50 rounded-xl p-6">
              <p className="text-custom-gray leading-relaxed mb-4">
                {policyData.sections[5].intro}
              </p>
              <address className="text-custom-gray mt-4 not-italic bg-white/30 p-4 rounded-lg">
                <span className="font-semibold text-custom-maroon">{policyData.sections[5].address.company}</span><br />
                {policyData.sections[5].address.lines.map((line, index) => (
                  <React.Fragment key={index}>
                    {line}<br />
                  </React.Fragment>
                ))}
              </address>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyPage; 